import React, { Suspense, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTexture } from '@react-three/drei';
import { ErrorBoundary } from 'react-error-boundary';
import { BufferGeometry, Color, DataTexture, LinearFilter, RepeatWrapping, Texture, Vector3 } from 'three';

import { Component, Member, MemberLabelOptions } from '@paragon/contract-library';
import { convertDegreesToRadians, Direction2D, Point2D } from '@paragon/geometry-library';
import { calculateMemberOverallLength, Segment3D } from '@paragon/three-shared';

import woodTextureUrl from '../../../resources/WoodFine0010.jpg';
import plainWoodTextureUrl from '../../../resources/WoodFinePlain.jpg';
import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { handleStandardSceneItemRightClick, useClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import {
  getColorForMemberSelector,
  getColorGradientForMemberSelector,
} from '../../store/selectors/getColorFromMemberDefinitionSelector';
import { getMemberRaycastType } from '../../store/selectors/getMemberRaycastType';
import { getMemberViewStatus } from '../../store/selectors/viewStatusSelectors';

export const MEMBER_COLOR = new Color(0xe0bb96);
export const MEMBER_EDGE_COLOR = 0x303030;
export const LAYOUT_MEMBER_EDGE_COLOR = 0x606060;

interface Props {
  readonly member: Member;
  readonly memberThreeJsGeometry: BufferGeometry | undefined;
  readonly label: React.JSX.Element | null;
}

export function MemberVisual({ label, member, memberThreeJsGeometry }: Props) {
  const dispatch = useAppDispatch();
  const viewStatus = useAppSelector(getMemberViewStatus)(member);
  const color = useAppSelector(getColorForMemberSelector)(member);
  const raycastType = useAppSelector(getMemberRaycastType)(member);
  const colorGradientForMember = useAppSelector((state) => getColorGradientForMemberSelector(state, member.guid));
  const woodTexture = useTexture(woodTextureUrl);
  const plainTexture = useTexture(plainWoodTextureUrl);

  useLayoutEffect(() => {
    if (!woodTexture) return;
    woodTexture.wrapS = RepeatWrapping;
    woodTexture.wrapT = RepeatWrapping;
    woodTexture.repeat.set(0.1, 0.1);
  }, [woodTexture]);

  useLayoutEffect(() => {
    if (!plainTexture) return;
    plainTexture.wrapS = RepeatWrapping;
    plainTexture.wrapT = RepeatWrapping;
    plainTexture.repeat.set(0.1, 0.1);
  }, [plainTexture]);

  const colorGradientTexture = useMemo(() => {
    return colorGradientForMember ? createTextureFromColorGradient(colorGradientForMember) : null;
  }, [colorGradientForMember]);

  useEffect(() => {
    return () => colorGradientTexture?.dispose();
  }, [colorGradientTexture]);

  const fallbackMemberTexture = <MemberTexture viewStatus={viewStatus} color={color} texture={null} />;

  const item = { type: raycastType, guid: member.guid, origin: 'fiber' } as const;
  const onClick = useClickItem({ type: raycastType, item: member });

  return (
    <group visible={viewStatus !== 'Hidden'}>
      {/* The userData-memberGuid is used by raycasting. */}
      {memberThreeJsGeometry ? (
        <mesh
          onClick={onClick}
          onContextMenu={async (event) => await dispatch(handleStandardSceneItemRightClick(event, item))}
          onPointerOver={(event) => {
            event.stopPropagation();
            dispatch(ensureHoverItemIsActive(item));
          }}
          onPointerOut={() => dispatch(clearHoverItemIfActive(item))}
          geometry={memberThreeJsGeometry}
        >
          {colorGradientTexture ? (
            <meshBasicMaterial
              key="colorGradient"
              transparent
              opacity={viewStatus === 'Transparent' ? 0.5 : 1}
              map={colorGradientTexture}
            />
          ) : (
            <ErrorBoundary fallback={fallbackMemberTexture}>
              <Suspense fallback={fallbackMemberTexture}>
                <MemberWoodTexture viewStatus={viewStatus} color={color} />
              </Suspense>
            </ErrorBoundary>
          )}
        </mesh>
      ) : null}

      {memberThreeJsGeometry ? (
        <lineSegments>
          <lineBasicMaterial color={MEMBER_EDGE_COLOR} linewidth={0.5} transparent />
          <edgesGeometry attach="geometry" args={[memberThreeJsGeometry]} />
        </lineSegments>
      ) : null}

      {label}
    </group>
  );
}

export function getMemberLabelText(
  member: Member,
  component: Component,
  memberLabelOptions: MemberLabelOptions,
  distanceFormatter: (number: number) => string,
) {
  const labelContent =
    member.memberType === 'Web' ? memberLabelOptions.webLabelContent : memberLabelOptions.nonWebLabelContent;

  switch (labelContent) {
    case 'MemberName':
      return member.name;
    case 'AssemblyName':
      return member.assemblyName;
    case 'OverallLength': {
      const overallLength = calculateMemberOverallLength(member, component);
      if (!overallLength) return null;
      return distanceFormatter(overallLength);
    }
    case 'Hidden':
      return null;
  }
}

export function getRotationForMemberControls(midline: Segment3D | null) {
  let rotation = 0;
  if (midline) {
    const midlineDirection = Direction2D.fromPoints(
      new Point2D(midline.start.x, midline.start.y),
      new Point2D(midline.end.x, midline.end.y),
    );
    const angleToRightInDegrees = Direction2D.right.angleTo(midlineDirection);
    const angleAdjustedForReadability =
      Math.abs(angleToRightInDegrees) > 90 ? angleToRightInDegrees - 180 : angleToRightInDegrees;
    rotation = convertDegreesToRadians(angleAdjustedForReadability);
  }
  return rotation;
}

/**
 * Gets the member label's position before it's adjusted for overlap with other labels.
 */
export function getInitialMemberLabelPosition(memberThreeJsGeometry: BufferGeometry | undefined) {
  if (!memberThreeJsGeometry) return null;
  const centroid = new Vector3()
    .addVectors(memberThreeJsGeometry!.boundingBox!.min, memberThreeJsGeometry!.boundingBox!.max)
    .divideScalar(2);
  return Point2D.fromObject(centroid);
}

function createTextureFromColorGradient(colorGradient: readonly Color[]) {
  const data = new Uint8Array(4 * colorGradient.length);

  for (let i = 0; i < colorGradient.length; i++) {
    const color = colorGradient[i];

    const dataOffset = i * 4;
    data[dataOffset] = Math.floor(color.r * 255);
    data[dataOffset + 1] = Math.floor(color.g * 255);
    data[dataOffset + 2] = Math.floor(color.b * 255);
    data[dataOffset + 3] = 255;
  }

  const texture = new DataTexture(data, colorGradient.length, 1);
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearFilter;
  texture.channel = 1;
  texture.needsUpdate = true;

  return texture;
}

type MemberTextureProps = {
  color: Color;
  viewStatus: ReturnType<ReturnType<typeof getMemberViewStatus>>;
  texture: Texture | null;
};

function MemberTexture(props: MemberTextureProps) {
  return (
    <meshBasicMaterial
      key="woodTexture"
      transparent
      opacity={props.viewStatus === 'Transparent' ? 0.5 : 1}
      color={props.color}
      map={props.texture}
    />
  );
}

useTexture.preload(woodTextureUrl);
useTexture.preload(plainWoodTextureUrl);

function MemberWoodTexture(props: Omit<MemberTextureProps, 'texture'>) {
  const woodTexture = useTexture(woodTextureUrl);
  const plainTexture = useTexture(plainWoodTextureUrl);

  useLayoutEffect(() => {
    if (!woodTexture) return;
    woodTexture.wrapS = RepeatWrapping;
    woodTexture.wrapT = RepeatWrapping;
    woodTexture.repeat.set(0.1, 0.1);
  }, [woodTexture]);

  useLayoutEffect(() => {
    if (!plainTexture) return;
    plainTexture.wrapS = RepeatWrapping;
    plainTexture.wrapT = RepeatWrapping;
    plainTexture.repeat.set(0.1, 0.1);
  }, [plainTexture]);

  return <MemberTexture {...props} texture={props.color.equals(MEMBER_COLOR) ? woodTexture : plainTexture} />;
}
